import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Form } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { CompanyInfoContext } from '../../../App';
import DataBlock from "../HomePage/DataBlock";
import DataCollectionOverviewCard from "../HomePage/DataCollectionOverviewCard";
import DataCollectionProgress from "../HomePage/DataCollectionProgress";
import ReportBlock from "../HomePage/ReportBlock";
import UnreportedDataPointsHome from "../HomePage/UnreportedDataPointsHome";
import AnomalySummaryHome from "../HomePage/AnomalySummaryHome";
import "./HomePage.scss";
import Overview from './Overview';
import useHasPermission from '../../../core/hooks/useHasPermission';
import {
    LS_HOME_PAGE_YEAR_KEY,
    LS_HOME_PAGE_YEAR_USER_SELECTED_KEY,
    startYear,
} from '../../../core/constants/common';
import {
    getCompanyInfoFromStorage,
    getDefaultSelectableYear,
    getMaxSelectableYear,
    isFinancialYearReporting,
} from '../../../core/utilities/assessmentPeriodUtils';

const resolveInitialSelectedYear = (companyInfo) => {
    const defaultYear = getDefaultSelectableYear(companyInfo);
    const maxYear = getMaxSelectableYear();
    const storedYear = localStorage.getItem(LS_HOME_PAGE_YEAR_KEY);
    const yearUserSelected = localStorage.getItem(LS_HOME_PAGE_YEAR_USER_SELECTED_KEY) === 'true';

    if (storedYear) {
        const yearNum = parseInt(storedYear, 10);
        if (yearNum >= startYear && yearNum <= maxYear) {
            const calendarYear = new Date().getFullYear();
            if (!yearUserSelected && !isFinancialYearReporting(companyInfo)) {
                return String(defaultYear);
            }
            if (
                !yearUserSelected &&
                isFinancialYearReporting(companyInfo) &&
                yearNum === calendarYear &&
                defaultYear === calendarYear + 1
            ) {
                return String(defaultYear);
            }
            return storedYear;
        }
    }
    return String(defaultYear);
};

const MONTH_LABEL_KEYS = { JANUARY: "January.label", FEBRUARY: "February.label", MARCH: "March.label", APRIL: "April.label", MAY: "May.label", JUNE: "June.label", JULY: "July.label", AUGUST: "August.label", SEPTEMBER: "September.label", OCTOBER: "October.label", NOVEMBER: "November.label", DECEMBER: "December.label" };
const MONTH_NUMS = { JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6, JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12 };

/** FY dropdown display label; option value stays the end year (e.g. value 2026 → "2025-2026"). */
export const getYearDropdownLabel = (year, isFinancialYear) => {
    const y = Number(year);
    if (isFinancialYear && !Number.isNaN(y)) {
        return `${y - 1}-${y}`;
    }
    return String(year);
};

const resolveCompanyInfo = (contextCompanyInfo) => {
    if (contextCompanyInfo && typeof contextCompanyInfo === 'object') {
        return contextCompanyInfo;
    }
    return getCompanyInfoFromStorage();
};

const HomePage = ({ t }) => {
    const { hasDoubleMaterialityViewPermission} = useHasPermission();
    const { companyInfo: contextCompanyInfo } = useContext(CompanyInfoContext);
    const year = new Date().getFullYear();
    
    const [selectedYear, setSelectedYear] = useState(() =>
        resolveInitialSelectedYear(getCompanyInfoFromStorage())
    );

    // Save to localStorage whenever selectedYear changes
    useEffect(() => {
        localStorage.setItem(LS_HOME_PAGE_YEAR_KEY, selectedYear);
    }, [selectedYear]);

    // Re-resolve default year once company FY settings load (Header fetches async after login)
    useEffect(() => {
        const companyInfo = resolveCompanyInfo(contextCompanyInfo);
        const resolvedYear = resolveInitialSelectedYear(companyInfo);
        setSelectedYear((prev) => (prev === resolvedYear ? prev : resolvedYear));
    }, [
        contextCompanyInfo?.dataFrequencyType,
        contextCompanyInfo?.frequencyType,
        contextCompanyInfo?.financialYearStart,
    ]);

    const companyInfo = resolveCompanyInfo(contextCompanyInfo);
    const isFinancialYear = isFinancialYearReporting(companyInfo);
    const maxSelectableYear = getMaxSelectableYear();

    // Memoize year options to avoid recreating on every render
    const yearOptions = useMemo(() => {
        const options = [];
        for (let i = maxSelectableYear; i >= startYear; i--) {
            const label = getYearDropdownLabel(i, isFinancialYear);
            options.push(<option value={i} key={i}>{label}</option>);
        }
        return options;
    }, [isFinancialYear, maxSelectableYear]);

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
        localStorage.setItem(LS_HOME_PAGE_YEAR_USER_SELECTED_KEY, 'true');
    };
    const periodRangeText = useMemo(() => {
        const y = Number(selectedYear) || year;
        if (isFinancialYear) {
            const startMonth = companyInfo?.financialYearStart || 'APRIL';
            const endMonth = companyInfo?.financialYearEnd || 'MARCH';
            const startLabel = MONTH_LABEL_KEYS[startMonth] ? t(MONTH_LABEL_KEYS[startMonth]) : t("April.label");
            const endLabel = MONTH_LABEL_KEYS[endMonth] ? t(MONTH_LABEL_KEYS[endMonth]) : t("March.label");
            const sm = MONTH_NUMS[startMonth] ?? 4;
            const em = MONTH_NUMS[endMonth] ?? 3;
            const startY = sm > em ? y - 1 : y;
            return `${startLabel} ${startY} ${t("HomePage.periodTo.label")} ${endLabel} ${y}`;
        }
        return `${t("January.label")} ${y} ${t("HomePage.periodTo.label")} ${t("December.label")} ${y}`;
    }, [selectedYear, isFinancialYear, year, t, companyInfo?.financialYearStart, companyInfo?.financialYearEnd]);

    const periodParams = useMemo(() => {
        const y = Number(selectedYear) || year;
        const frequencyType = isFinancialYear ? 'FY' : 'CY';
        if (isFinancialYear) {
            const startMonth = companyInfo?.financialYearStart || 'APRIL';
            const endMonth = companyInfo?.financialYearEnd || 'MARCH';
            const sm = MONTH_NUMS[startMonth] ?? 4;
            const em = MONTH_NUMS[endMonth] ?? 3;
            const startY = sm > em ? y - 1 : y;
            const lastDay = new Date(y, em, 0).getDate();
            return {
                periodStart: `${startY}-${String(sm).padStart(2, '0')}-01`,
                periodEnd: `${y}-${String(em).padStart(2, '0')}-${lastDay}`,
                frequencyType,
            };
        }
        return { periodStart: `${y}-01-01`, periodEnd: `${y}-12-31`, frequencyType };
    }, [selectedYear, isFinancialYear, year, companyInfo?.financialYearStart, companyInfo?.financialYearEnd]);

    const displayYear = useMemo(
        () => getYearDropdownLabel(selectedYear, isFinancialYear),
        [selectedYear, isFinancialYear]
    );

    return (
        <div className="HomePageCard">
            <div className="HomePageHeader" style={{position: 'sticky', top: '50px', zIndex: '1000'}}>
                <div className="HomePageHeaderContent">
                    <div className="HomePageHeaderLeft"></div>
                    <div className="HomePageHeaderRight">
                        <div className="home-page-period-range">
                            <span className="home-page-period-label">{t("Period.label")}:</span>
                            <span className="home-page-period-value">{periodRangeText}</span>
                        </div>
                        <Form.Group className="mb-0">
                            <Form.Select 
                                value={selectedYear} 
                                onChange={handleYearChange}
                                className="year-filter-select"
                            >
                                {yearOptions}
                            </Form.Select>
                        </Form.Group>
                    </div>
                </div>
            </div>
            <div className="row m-0">
                <div className="col-lg-6 p-2">
                    <DataBlock selectedYear={selectedYear} displayYear={displayYear} />
                    {/* <DataCollectionProgress
                        selectedYear={selectedYear}
                        displayYear={displayYear}
                        periodParams={periodParams}
                    /> */}
                </div>
                <div className="col-lg-6 p-2">
                    {/* <Calendar /> */}
                    {/* <ImplementationGuide displayedFor="HOME"/> */}
                    <ReportBlock selectedYear={selectedYear} periodParams={periodParams} />
                    <AnomalySummaryHome periodParams={periodParams} displayYear={displayYear} />
                    <UnreportedDataPointsHome defaultPeriodFilter={periodParams.periodStart} />
                    {/* {hasDoubleMaterialityViewPermission() && <Overview selectedYear={selectedYear} />} */}
                </div>
            </div>
            <div className="row m-0">
                <div className="col-lg-12 p-2">
                    <DataCollectionOverviewCard selectedYear={selectedYear} displayYear={displayYear} />
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(HomePage);
